'use strict'

// PWA OOTB Hooks
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from './use-multi-site'

import AlertMgr from '../custom-api/alert-custom-api'

/**
 * Custom hook to manage alert operations using the AlertMgr API.
 *
 * @returns {Object} An object containing methods to interact with the AlertMgr API.
 * @returns {Function} sendAlert - Sends an alert using the provided alert object and returns the result.
 *
 * @property {Function} sendAlert - Sends an alert using the provided alert object and returns the result.
 *
 * @async
 * @param {Object} alertObj - The alert object containing the details of the alert to be sent.
 * @returns {Promise<Object>} A promise that resolves to an object containing the success status and message of the alert operation.
 * @property {boolean} success - Indicates whether the alert was sent successfully.
 * @property {string} message - Provides additional information about the alert operation.
 */
export const useAlertMgr = () => {
    // Create an instance of the Custom API class
    const {getTokenWhenReady} = useAccessToken()
    const {app: appConfig} = getConfig()
    const {site, locale} = useMultiSite()
    const api = new AlertMgr({
        parameters: {
            siteId: site.id,
            organizationId: appConfig.commerceAPI.parameters.organizationId,
            locale: locale.id
        }
    })

    return {
        /**
         * Sends an alert using the provided alert object and returns the result.
         *
         * @async
         * @param {Object} alertObj - The alert object containing the details of the alert to be sent.
         * @returns {Promise<Object>} A promise that resolves to an object containing the success status and message of the alert operation.
         * @property {boolean} success - Indicates whether the alert was sent successfully.
         * @property {string} message - Provides additional information about the alert operation.
         */
        async sendAlert(alertObj) {
            let result = {
                success: false,
                message: ''
            }

            try {
                const accessToken = await getTokenWhenReady()
                const alertResult = await api.sendAlert(alertObj, accessToken)

                result.success = alertResult.success
                result.message = alertResult.message
            } catch (error) {
                result.msg = error.message
                result.success = false
            }

            return result
        }
    }
}
